import { createAction, createAsyncThunk } from '@reduxjs/toolkit'
import { change } from 'redux-form'
import moment from 'moment'
import get from 'lodash/get'
import includes from 'lodash/includes'
import { LoginErrorCode, LookupType, PasswordState, UserVerifyResult } from '@lk/api'
import mainTranslate, { LanguageService } from 'loankeeper-common/helpers/language'
import { setToken } from 'loankeeper-common/api/callApi'
import { translate as translateBase } from 'loankeeper-common/helpers'

import { actions as LKModalActions } from '../../features/shared/LKModal'
import { REGULAR_CHANGE_PASSWORD_MODAL } from '../../features/ChangePasswordForm'
import { AUTH_CHANGE_PASSWORD_MODAL } from '../../features/ChangePasswordForm/constants'
import { FORM_NAME, CHANGE_DATE_TIME_MODAL } from '../../features/Auth/constants'
import { getMaxAllowedTimeDifferenceWithServer } from '../config'
import { storeOTP, storeAuth, forgetOTP, forgetAuth, getPersistedSession } from '../persist'
import * as openedPagesActions from '../persist/openedPages.actions'
import * as tabsActions from '../persist/tabs.actions'
import { loadCitiesByCountryId, loadLookupsByType } from '../lookups'
import { loadPermissions } from '../permissions'
import { repeatedlyLoadLivedata, getLivedata, loadLivedata, isLivedataLoaded } from '../livedata'
import { isUserLockedByOther } from '../livedata/livedata.helpers'
import { ThunkApi } from '../index.d'
import { loadUserConfigs, getUserID } from '../userConfig'

const translate = (key: string): string => translateBase(`frm_web_messages-${key}`)
const translateLKMessage = (key: string): string => translateBase(`LKMESSAGE-${key}`)

const LOGIN_SMS_OTP = '0000'

let logoutCallbacks = []

export function addLogoutCallback(fn) {
  logoutCallbacks.push(fn)
}

export const setSTKToken = createAction('[AUTH] SET_TOKEN', (token: string) => {
  // TODO: this has to be removed in the future
  setToken(token)
  return {
    payload: token,
  }
})

interface LogoutAttributes {
  hard: boolean
  reason?: string
}

export const logout = createAsyncThunk<string, LogoutAttributes, ThunkApi>(
  '[AUTH] LOGOUT',
  async ({ hard, reason }, { dispatch, getState, extra, rejectWithValue }) => {
    const { username } = getPersistedSession(getState())

    const result = await extra.api.auth.logout()

    if (result.success && hard) {
      dispatch(forgetOTP(username))
    }

    extra.storage.remove('LOAN_CALC_DATA')

    logoutCallbacks.forEach((fn) => fn())
    logoutCallbacks = []

    dispatch([forgetAuth(), openedPagesActions.reset(), tabsActions.reset()])

    dispatch(setSTKToken(null))

    if (!result.success) {
      return rejectWithValue('error happened while logging out')
    }

    return reason
  },
)

export const lock = (reason?: string) => logout({ hard: false, reason })

export const setReady = createAction('[AUTH] SET_APP_READY')

export const compareServerDateToClientDevice = () => (dispatch, getState) => {
  const allowedTimeDifference = getMaxAllowedTimeDifferenceWithServer(getState())

  const livedata = getLivedata(getState())

  const { Cur_Date } = livedata.OpenDayStatus

  const serverTime = moment(Cur_Date)
  const differenceWithServerTimeInSeconds = moment().diff(serverTime, 'seconds')

  if (Math.abs(differenceWithServerTimeInSeconds) > allowedTimeDifference) {
    dispatch(
      LKModalActions.open({
        name: CHANGE_DATE_TIME_MODAL,
        message: Cur_Date,
      }),
    )
  }
}

export const loadRequiredData = createAsyncThunk<void, void, ThunkApi>(
  '[AUTH] LOAD_REQUIRED_DATA',
  async (_, { getState, dispatch }) => {
    const livedataLoaded = isLivedataLoaded(getState())

    const [userConfigResult] = await Promise.all([
      dispatch(loadUserConfigs()),
      dispatch(loadPermissions()),
      dispatch(loadLookupsByType(LookupType.System)),
      !livedataLoaded && dispatch(loadLivedata()), // this prevents us to load same data twice
    ])

    const state = getState()

    // we should start repeatedly loading livedata if user is not locked
    if (!isUserLockedByOther(getLivedata(state), getUserID(state))) {
      dispatch(repeatedlyLoadLivedata())
    }

    dispatch(compareServerDateToClientDevice())

    if (loadUserConfigs.fulfilled.match(userConfigResult as any)) {
      const { payload: configPayload } = userConfigResult as any
      const countryID = get(configPayload, 'user.MyCountryID')

      if (countryID) {
        dispatch(loadCitiesByCountryId(countryID))
      }

      // set language
      const lang = get(configPayload, 'user.Lang', 'geo')
      LanguageService.setLanguage(lang.toLowerCase())
    }
  },
)

export const refreshSTK = createAsyncThunk<string, string, ThunkApi>(
  '[AUTH] REFRESH_STK',
  async (STK: string, { dispatch, rejectWithValue }) => {
    dispatch(setSTKToken(STK))

    // for checking token
    const livedataActionResult = await dispatch(loadLivedata())

    if (loadLivedata.rejected.match(livedataActionResult as any)) {
      await dispatch(logout({ hard: false }))
      return rejectWithValue('failed to refresh STK')
    }

    await dispatch(loadRequiredData())

    return STK
  },
)

const ERROR_NO_ACTION_CODES = [
  LoginErrorCode.ERR_INVALID_USER_PASS,
  LoginErrorCode.ERR_USER_LOCKED,
  LoginErrorCode.ERR_INVALID_OTP,
  LoginErrorCode.ERR_INVALID_OTP_HASH,
  LoginErrorCode.ERR_CLOSE_DAY_RUNNING,
  LoginErrorCode.ERR_IP_ACCESS_DENIED,
  LoginErrorCode.ERR_LOGIN_FAILED,
]

const getLoginFailureMessage = (error: { code: LoginErrorCode; message: string }): string => {
  switch (error.code) {
    case LoginErrorCode.ERR_INVALID_USER_PASS:
    case LoginErrorCode.ERR_USER_LOCKED:
    case LoginErrorCode.ERR_INVALID_OTP:
    case LoginErrorCode.ERR_HASH_EXPIRED:
    case LoginErrorCode.ERR_CLOSE_DAY_RUNNING:
    case LoginErrorCode.ERR_IP_ACCESS_DENIED:
    case LoginErrorCode.ERR_INVALID_HASH:
    case LoginErrorCode.ERR_LOGIN_FAILED: {
      return mainTranslate(`frm_web_messages-${error.message}`)
    }
    case LoginErrorCode.ERR_USER_WEB_ACCESS_DENIED:
    case LoginErrorCode.ERR_INVALID_SMS_OTP:
    case LoginErrorCode.ERR_INVALID_OTP_HASH:
    case LoginErrorCode.ERR_INVALID_USER_PHONE:
    case LoginErrorCode.ERR_INVALID_USER_PHONE_CODE:
    case LoginErrorCode.UNKNOWN: {
      return mainTranslate(`LKMESSAGE-${error.message}`)
    }
  }
}

interface LoginAttributes {
  username: string
  password: string
  otp?: string
  otpHash?: string
}

export const login = createAsyncThunk<string, LoginAttributes, ThunkApi>(
  '[AUTH] LOGIN',
  async ({ username, password, otp, otpHash }, { dispatch, extra, rejectWithValue }) => {
    const result = await extra.api.auth.login(username, password, otp, otpHash)

    if (result.success) {
      const { data } = result

      dispatch(
        [
          setSTKToken(data.STK),
          storeAuth({
            username,
            expire: data.STKExpire * 1000,
            loginResult: data,
          }),
          // when sending otphash server doesn't return otphash & otphashExpire
          data.OTPHash &&
            storeOTP({
              username,
              hash: data.OTPHash,
              expire: data.OTPHashExpire * 1000,
            }),
        ].filter(Boolean),
      )

      await dispatch(loadRequiredData())

      if (data.PassExpire === PasswordState.Expired) {
        dispatch(
          LKModalActions.open({
            name: REGULAR_CHANGE_PASSWORD_MODAL,
            message: '',
            lockOnClose: true,
            allowSkip: true,
          }),
        )
      }

      return data.STK
    } else {
      const { error } = result

      if (error.code === LoginErrorCode.ERR_USER_PASSWORD_EXPIRED_CHANGE) {
        dispatch(
          LKModalActions.open({
            name: AUTH_CHANGE_PASSWORD_MODAL,
            username,
            message: mainTranslate(`LKMESSAGE-${error.message}`),
            passwordRules: error.passwordRules,
          }),
        )
        return rejectWithValue({ message: '' })
      }

      // Set default message to Georgian to show error message in proper form
      // LanguageService.setLanguage('geo')

      let formErrors

      if (
        includes(
          [LoginErrorCode.ERR_HASH_EXPIRED, LoginErrorCode.ERR_INVALID_HASH, LoginErrorCode.ERR_INVALID_SMS_OTP],
          error.code,
        )
      ) {
        dispatch(forgetOTP(username))
        dispatch(change(FORM_NAME, 'otp', ''))
        formErrors = { otp: 'invalid otp code' }
      } else if (!includes(ERROR_NO_ACTION_CODES, error.code)) {
        dispatch(change(FORM_NAME, 'username', ''))
        dispatch(change(FORM_NAME, 'password', ''))
        formErrors = { username: 'invalid username or password' }
      }

      return rejectWithValue({ message: getLoginFailureMessage(error as any), formErrors })
    }
  },
)

interface SendLoginSmsAttributes {
  username: string
  password: string
}

export const sendLoginSms = createAsyncThunk<void, SendLoginSmsAttributes, ThunkApi>(
  '[AUTH] SEND_LOGIN_SMS',
  async ({ username, password }, { dispatch, extra, rejectWithValue }) => {
    const result = await extra.api.auth.login(username, password, LOGIN_SMS_OTP, null)

    if (result.success) {
      dispatch(LKModalActions.openInfo(translate('MSG_SMS_OTP_SENT')))
      return
    }

    const { error } = result

    if (includes([LoginErrorCode.ERR_INVALID_USER_PASS], error.code)) {
      dispatch(change(FORM_NAME, 'username', ''))
      dispatch(change(FORM_NAME, 'password', ''))
    }

    dispatch(LKModalActions.openError(translate(error.message as string)))
    return rejectWithValue({ message: getLoginFailureMessage(error as any) })
  },
)

export const setSTKExpired = createAction<boolean>('[AUTH] SET_STK_EXPIRED')

interface ChangePasswordForcedAttributes {
  username: string
  password: string
  newPassword: string
  otp: string
}

export const changePasswordForced = createAsyncThunk<void, ChangePasswordForcedAttributes, ThunkApi>(
  '[AUTH] CHANGE_PASSWORD_FORCED',
  async ({ username, password, newPassword, otp }, { dispatch, extra, rejectWithValue }) => {
    const result = await extra.api.auth.changePasswordForced(username, password, newPassword, otp)

    if (!result.success) {
      const { error } = result

      if (includes([LoginErrorCode.ERR_HASH_EXPIRED, LoginErrorCode.ERR_INVALID_HASH], error.code)) {
        return rejectWithValue('invalid otp code')
      } else if (!includes(ERROR_NO_ACTION_CODES, error.code)) {
        return rejectWithValue('invalid username or password')
      }

      return rejectWithValue('Failed to change password')
    }
  },
)

export const verifyPasswordChangeOTP = createAsyncThunk<any, { username: string; mobile: string }, ThunkApi>(
  '[AUTH] VERIFY_PASSWORD_CHANGE_OTP',
  async ({ username, mobile }, { dispatch, extra, rejectWithValue }) => {
    const result = await extra.api.auth.verifyPasswordChangeOTP(username, mobile)
    if (!result.success) {
      const { error } = result
      return rejectWithValue({ message: mainTranslate(`LKMESSAGE-${error.message}`) })
    } else {
      return result
    }
  },
)

interface ResetPasswordParams {
  username: string
  cellphone: string
  newpass: string
  code: string
}

export const resetPassword = createAsyncThunk<any, ResetPasswordParams, ThunkApi>(
  '[AUTH] RESET_PASSWORD',
  async (params, { dispatch, extra, rejectWithValue }) => {
    const { username, cellphone, newpass, code } = params
    const result = await extra.api.auth.resetPassword(username, cellphone, newpass, code)
    if (!result.success) {
      const { error } = result
      return rejectWithValue({ message: mainTranslate(`LKMESSAGE-${error.message}`) })
    }

    return result
  },
)
